import { useEffect, useState } from 'react';
import { NavLink, Outlet, Link, useLocation } from 'react-router-dom';
import { business } from './content';
import { useLanguage } from './language/LanguageContext';

function AppLayout() {
  const { language, languages, setLanguage, t } = useLanguage();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const location = useLocation();
  const links = [
    { to: '/', label: t.nav.home, end: true },
    { to: '/privacy-security', label: t.nav.privacy },
    { to: '/terms-conditions', label: t.nav.terms },
    { to: '/support', label: t.nav.support },
    { to: '/delete-account', label: t.nav.deleteAccount }
  ];

  useEffect(() => {
    setIsSidebarOpen(false);
    setIsLanguageDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.classList.toggle('menu-open', isSidebarOpen);

    return () => {
      document.body.classList.remove('menu-open');
    };
  }, [isSidebarOpen]);

  useEffect(() => {
    if (!isSidebarOpen) return undefined;

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setIsSidebarOpen(false);
      }
    }

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSidebarOpen]);

  const activeLanguage = languages.find((item) => item.code === language) || languages[0];

  function handleSidebarLanguageChange(nextLanguage) {
    setLanguage(nextLanguage);
    setIsLanguageDropdownOpen(false);
  }

  return (
    <div className="site-shell">
      <header className="site-header">
        <nav className="nav-wrap" aria-label={t.nav.aria}>
          <Link className="brand" to="/">
            <span className="brand-mark">
              <img src="/citybarber-nav-logo.png" alt="" />
            </span>
            <span>
              <strong>{business.appName}</strong>
              <small>{t.nav.official}</small>
            </span>
          </Link>

          <div className="nav-actions">
            <div className="nav-links">
              {links.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.end}
                  className={({ isActive }) => (isActive ? 'active' : undefined)}
                >
                  {link.label}
                </NavLink>
              ))}
            </div>

            <label className="language-select">
              <span>{t.nav.language}</span>
              <select value={language} onChange={(event) => setLanguage(event.target.value)}>
                {languages.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.shortLabel} - {item.label}
                  </option>
                ))}
              </select>
            </label>

            <button
              className="menu-button"
              type="button"
              aria-label={t.nav.menu}
              aria-expanded={isSidebarOpen}
              aria-controls="site-sidebar"
              onClick={() => setIsSidebarOpen(true)}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </nav>
      </header>

      <div
        className={`sidebar-overlay ${isSidebarOpen ? 'show' : ''}`}
        role="presentation"
        aria-hidden="true"
        onClick={() => setIsSidebarOpen(false)}
      />

      <aside
        id="site-sidebar"
        className={`site-sidebar ${isSidebarOpen ? 'open' : ''}`}
        role={isSidebarOpen ? 'dialog' : undefined}
        aria-modal={isSidebarOpen ? 'true' : undefined}
        aria-hidden={!isSidebarOpen}
      >
        <div className="sidebar-head">
          <Link className="brand" to="/">
            <span className="brand-mark">
              <img src="/citybarber-nav-logo.png" alt="" />
            </span>
            <span>
              <strong>{business.appName}</strong>
              <small>{t.nav.official}</small>
            </span>
          </Link>

          <button
            className="sidebar-close"
            type="button"
            aria-label={t.nav.close}
            onClick={() => setIsSidebarOpen(false)}
          >
            ×
          </button>
        </div>

        <div className="sidebar-links">
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              className={({ isActive }) => (isActive ? 'active' : undefined)}
            >
              {link.label}
            </NavLink>
          ))}
        </div>

        <div className="sidebar-settings-group">
          <div className="sidebar-language-row">
            <div className="sidebar-language-left">
              <span>
                <strong>{t.nav.language}</strong>
                <small>{activeLanguage?.label}</small>
              </span>
            </div>

            <button
              className="sidebar-language-trigger"
              type="button"
              onClick={() => setIsLanguageDropdownOpen((current) => !current)}
              aria-expanded={isLanguageDropdownOpen}
            >
              <span>{activeLanguage?.label}</span>
              <span aria-hidden="true">{isLanguageDropdownOpen ? '⌃' : '⌄'}</span>
            </button>
          </div>

          {isLanguageDropdownOpen && (
            <div className="sidebar-language-menu">
              {languages.map((item) => {
                const isActive = language === item.code;

                return (
                  <button
                    key={item.code}
                    type="button"
                    className={isActive ? 'active' : undefined}
                    onClick={() => handleSidebarLanguageChange(item.code)}
                  >
                    <span>{item.label}</span>
                    {isActive && <strong aria-hidden="true">✓</strong>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </aside>

      <main>
        <Outlet />
      </main>

      <footer className="site-footer">
        <div>
          <strong>{business.appName}</strong>
          <p>{t.footer.tagline}</p>
        </div>
        <div className="footer-links">
          <Link to="/privacy-security">{t.footer.privacy}</Link>
          <Link to="/terms-conditions">{t.footer.terms}</Link>
          <Link to="/support">{t.footer.support}</Link>
          <Link to="/delete-account">{t.nav.deleteAccount}</Link>
        </div>
      </footer>
    </div>
  );
}

export default AppLayout;
